import { useEffect, useMemo, useRef, useState } from "react";
import { SEO } from "../seo/SEO";
import { useFullComplementMDM } from "../hooks/useFetch/useComplement";
// import { useNotes } from "../hooks/useFetch/useNotes";
import { usePodcastPlayer } from "../hooks/useAudios/usePodcastPlayer";
import { API_BASE_URL_BASE } from "../config/api";
import { getChapterColor } from "../components/audios/chapterColors";
import { PodcastHeader } from "../components/audios/PodcastHeader";
import { SponsorBanner } from "../components/audios/SponsorBanner";
// import { ReadNowButton } from "../components/audios/ReadNowButton";
import { ChapterList } from "../components/audios/ChapterList";
import { QueueBar } from "../components/audios/QueueBar";
import { MiniPlayer } from "../components/audios/MiniPlayer";
import { PlayerScreen } from "../components/audios/PlayerScreen";
import { PodcastFooter } from "../components/audios/PodcastFooter";
// import { FarandulaSection } from "../components/audios/FarandulaSection";
// import { SportsScoreboard } from "../components/audios/SportsScoreboard";
import { formatDurationLong } from "../utils/formatTime";

const BOLETINES = [
  { id: 1000376, name: "Agenda Nacional" },
  { id: 1000372, name: "Círculo Rojo" },
  { id: 1000375, name: "Economía y Finanzas" },
  { id: 22252, name: "Internacional" },
  { id: 1000886, name: "Zettabyte" },
  { id: 22470, name: "Farándula" },
  { id: 22471, name: "Deportes" },
];

const buildChapters = (data) => {
  if (!data) return [];

  return BOLETINES.map((boletin) => {
    const val = data[boletin.id];
    if (!val) return null;

    const audioResumen = val?.AudioResumidoNV?.url ? val.AudioResumidoNV : null;
    const audioCompleto = val?.AudioCompletoNV?.url ? val.AudioCompletoNV : null;
    if (!audioResumen && !audioCompleto) return null;

    return {
      id: boletin.id.toString(),
      name: boletin.name,
      fecha: val.lastUpdate,
      variants: { resumen: audioResumen, completo: audioCompleto },
    };
  })
    .filter(Boolean)
    .map((chapter, index) => ({ ...chapter, color: getChapterColor(index) }));
};

// const NOTES_DOCUMENT_ID = 1100058;
const FARANDULA_SECTION_ID = 22470;
const DEPORTES_SECTION_ID = 22471;

const buildSpecialChapter = (data, id, name, color) => {
  const val = data?.[id];
  if (!val) return null;

  const audioResumen = val?.AudioResumidoNV?.url ? val.AudioResumidoNV : null;
  const audioCompleto = val?.AudioCompletoNV?.url ? val.AudioCompletoNV : null;
  if (!audioResumen && !audioCompleto) return null;

  return {
    id: id.toString(),
    name,
    fecha: val.lastUpdate,
    variants: { resumen: audioResumen, completo: audioCompleto },
    color,
  };
};

const buildTrack = (chapter, type) => {
  const variant = chapter.variants[type];
  if (!variant?.url) return null;

  return {
    key: `${chapter.id}-${type}`,
    chapterId: chapter.id,
    type,
    name: chapter.name,
    color: chapter.color,
    url: `${API_BASE_URL_BASE}${variant.url}`,
  };
};

export default function Home() {
  const { data, loading } = useFullComplementMDM();
  const [isExpanded, setIsExpanded] = useState();
  const [durations, setDurations] = useState({});
  const [queueKeys, setQueueKeys] = useState([]);
  const probedKeys = useRef(new Set());
  const probeInstances = useRef([]);

  const chapters = useMemo(() => buildChapters(data), [data]);

  const farandulaChapter = useMemo(
    () => buildSpecialChapter(data, FARANDULA_SECTION_ID, "Farándula", "#ff8ac4"),
    [data]
  );
  const deportesChapter = useMemo(
    () => buildSpecialChapter(data, DEPORTES_SECTION_ID, "Deportes", "#00c9a7"),
    [data]
  );

  const allChapters = useMemo(
    () => [...chapters, farandulaChapter, deportesChapter].filter(Boolean),
    [chapters, farandulaChapter, deportesChapter]
  );

  // const fecha = useMemo(() => {
  //   const today = new Date();
  //   return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
  //     today.getDate()
  //   ).padStart(2, "0")}`;
  // }, []);

  // const { data: farandulaNotes } = useNotes({
  //   id_documento: NOTES_DOCUMENT_ID,
  //   id_seccion: FARANDULA_SECTION_ID,
  //   fecha,
  // });

  // const { data: deportesNotes } = useNotes({
  //   id_documento: NOTES_DOCUMENT_ID,
  //   id_seccion: DEPORTES_SECTION_ID,
  //   fecha,
  // });

  const player = usePodcastPlayer({
    onDurationKnown: (key, value) => {
      setDurations((prev) => ({ ...prev, [key]: value }));
    },
  });

  useEffect(() => {
    chapters.forEach((chapter) => {
      ["resumen", "completo"].forEach((type) => {
        const track = buildTrack(chapter, type);
        if (!track || probedKeys.current.has(track.key)) return;
        probedKeys.current.add(track.key);

        const probe = new Audio(track.url);
        probe.preload = "metadata";
        probeInstances.current.push(probe);

        const cleanup = () => {
          probe.removeEventListener("loadedmetadata", handleMeta);
          probe.removeEventListener("error", cleanup);
        };
        const handleMeta = () => {
          setDurations((prev) => ({ ...prev, [track.key]: probe.duration }));
          cleanup();
        };

        probe.addEventListener("loadedmetadata", handleMeta);
        probe.addEventListener("error", cleanup);
      });
    });
  }, [chapters]);

  const audioStats = useMemo(() => {
    let totalAudios = 0;
    let totalSeconds = 0;

    chapters.forEach((chapter) => {
      ["resumen", "completo"].forEach((type) => {
        if (!chapter.variants[type]) return;
        totalAudios += 1;
        const key = `${chapter.id}-${type}`;
        totalSeconds += durations[key] || 0;
      });
    });

    return { totalPodcasts: chapters.length, totalAudios, totalSeconds };
  }, [chapters, durations]);

  const queueTracks = useMemo(
    () =>
      queueKeys
        .map((key) => {
          const [chapterId, type] = key.split("-");
          const chapter = chapters.find((c) => c.id === chapterId);
          return chapter ? buildTrack(chapter, type) : null;
        })
        .filter(Boolean),
    [queueKeys, chapters]
  );

  const buildBrowseList = (preferredType) =>
    chapters
      .map((chapter) =>
        buildTrack(chapter, chapter.variants[preferredType] ? preferredType : preferredType === "resumen" ? "completo" : "resumen")
      )
      .filter(Boolean);

  const handlePlayVariant = (chapterIndex, type) => {
    const list = buildBrowseList(type);
    player.playList(list, chapterIndex);
    setIsExpanded(true);
  };

  // const handlePlaySpecialChapter = (chapter) => {
  //   if (!chapter) return;
  //   const track = buildTrack(chapter, chapter.variants.resumen ? "resumen" : "completo");
  //   if (!track) return;
  //   player.playList([track], 0);
  //   setIsExpanded(true);
  // };

  const handleToggleQueue = (chapterId, type) => {
    const key = `${chapterId}-${type}`;
    setQueueKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handlePlayQueue = () => {
    if (queueTracks.length === 0) return;
    player.playList(queueTracks, 0);
    setIsExpanded(true);
  };

  const handleClearQueue = () => setQueueKeys([]);

  const handleSwitchType = (newType) => {
    if (player.currentIndex == null || !player.current) return;
    const chapter = allChapters.find((c) => c.id === player.current.chapterId);
    const newTrack = chapter ? buildTrack(chapter, newType) : null;
    if (!newTrack) return;

    const updatedList = [...player.activeTracks];
    updatedList[player.currentIndex] = newTrack;
    player.playList(updatedList, player.currentIndex);
  };

  return (
    <>
      <SEO
        title="Nuestra Visión TV"
        description="Escucha cada mañana el resumen informativo en formato podcast"
      />

      <audio ref={player.audioRef} src={player.current?.url} {...player.audioEventHandlers} />

      <section
        className="min-h-screen px-0 pb-10"
        style={{ background: "#160e2f", paddingTop: "var(--podcast-header-h, 160px)" }}
      >
        <PodcastHeader />

        {!isExpanded && (
          <div
            className="sticky z-[65] px-4 pt-0 pb-1"
            style={{ top: "var(--podcast-header-h, 160px)", background: "#160e2f" }}
          >
            <div
              className="max-w-xl mx-auto rounded-[22px] p-2"
              style={{
                background: "linear-gradient(150deg,#1e1140,#2b0f3d 60%,#3d0f38)",
                border: "1px solid rgba(255,255,255,.09)",
              }}
            >
              <SponsorBanner />
            </div>
          </div>
        )}

        <div className="max-w-xl mx-auto pt-1 px-4">
          {/* <div className="mt-3">
            <ReadNowButton />
          </div> */}

          <div className="flex items-center justify-between mt-2 mb-">
            <h3 className="text-white font-bold text-lg">Episodios de hoy</h3>
            <span className="text-white/40 text-xs">Crea tu lista</span>
          </div>

          {!loading && chapters.length > 0 && (
            <div className="flex items-center gap-2 text-white/40 text-xs mb-3">
              <span>
                {audioStats.totalPodcasts} podcast{audioStats.totalPodcasts === 1 ? "" : "s"}
              </span>
              <span className="w-1 h-1 rounded-full bg-white/20" />
              <span>{formatDurationLong(audioStats.totalSeconds)} en total</span>
            </div>
          )}

          <QueueBar count={queueTracks.length} onPlayQueue={handlePlayQueue} onClear={handleClearQueue} />

          {loading ? (
            <p className="text-center text-white/40 text-sm italic py-10">Cargando audios...</p>
          ) : (
            <ChapterList
              chapters={chapters}
              currentTrack={player.current}
              isPlaying={player.isPlaying}
              currentTime={player.currentTime}
              duration={player.duration}
              completedKeys={player.completedKeys}
              durations={durations}
              queueKeys={queueKeys}
              onPlayVariant={handlePlayVariant}
              onToggleQueue={handleToggleQueue}
            />
          )}

          {/* <FarandulaSection
            notes={farandulaNotes}
            hasAudio={Boolean(farandulaChapter)}
            onPlay={() => handlePlaySpecialChapter(farandulaChapter)}
          />

          <SportsScoreboard
            notes={deportesNotes}
            hasAudio={Boolean(deportesChapter)}
            onPlay={() => handlePlaySpecialChapter(deportesChapter)}
          /> */}
        </div>

        <PodcastFooter />
      </section>

      {player.current && !isExpanded && (
        <MiniPlayer
          track={player.current}
          currentTime={player.currentTime}
          duration={player.duration}
          isPlaying={player.isPlaying}
          onTogglePlay={player.togglePlay}
          onExpand={() => setIsExpanded(true)}
          onClose={player.stop}
        />
      )}

      {player.current && isExpanded && (
        <PlayerScreen
          track={player.current}
          tracks={player.activeTracks}
          chapters={allChapters}
          currentIndex={player.currentIndex}
          currentTime={player.currentTime}
          duration={player.duration}
          isPlaying={player.isPlaying}
          playbackRate={player.playbackRate}
          onClose={() => setIsExpanded(false)}
          onTogglePlay={player.togglePlay}
          onSeek={player.seek}
          onSkip={player.skip}
          onPrev={player.prev}
          onNext={player.next}
          onSetRate={player.setPlaybackRate}
          onJump={(index) => player.playAt(index)}
          onSwitchType={handleSwitchType}
          onMoveTrack={player.moveTrack}
          onRemoveTrack={player.removeTrack}
        />
      )}
    </>
  );
}
